import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { 
  Server, Users, Briefcase, Zap, Target, Award, 
  TrendingUp, Shield, Globe, ArrowRight, CheckCircle,
  ShoppingBag, HeartPulse, Factory, Cloud, Car,
  Truck, Landmark, Network, Database, UserCog, Cog,
  Building2
} from 'lucide-react';
import { clientLogos, whyInfotron, caseStudies, testimonials } from '../data/mockData';

// cubic-bezier(0.22, 1, 0.36, 1) — easeOutQuint approximation
const easeOutPortal = (t) => {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 5);
};
const lerp = (a, b, t) => a + (b - a) * t;
// Smoothstep — eased 0→1 across [a, b]
const smoothstep = (a, b, t) => {
  if (b === a) return t < a ? 0 : 1;
  const x = Math.max(0, Math.min(1, (t - a) / (b - a)));
  return x * x * (3 - 2 * x);
};

// ─── Industries We Serve — data + card ────────────────────────────────────
const industries = [
  { name: 'E-commerce',                outcome: 'Scalable platforms',           Icon: ShoppingBag },
  { name: 'Healthcare',                outcome: 'Secure systems',               Icon: HeartPulse  },
  { name: 'SaaS',                      outcome: 'Rapid product delivery',       Icon: Cloud       },
  { name: 'Financial Services',        outcome: 'Compliant infrastructure',     Icon: Shield      },
  { name: 'Infrastructure & Utilities',outcome: 'Resilient systems',            Icon: Network     },
  { name: 'Data Centers',              outcome: 'High-availability environments', Icon: Database  },
  { name: 'Manufacturing',             outcome: 'Reliable operations',          Icon: Factory     },
  { name: 'Automotive',                outcome: 'High-performance systems',     Icon: Car         },
  { name: 'Logistics',                 outcome: 'Optimized workflows',          Icon: Truck       },
  { name: 'Public Sector',             outcome: 'Secure, scalable delivery',    Icon: Landmark    },
  { name: 'HR & EOR',                  outcome: 'Workforce scalability',        Icon: UserCog     },
  { name: 'Industrial Systems',        outcome: 'Process optimization',         Icon: Cog         },
];

// ─── Engagement Models — mirrors Services dropdown order exactly ──────────
const engagementModels = [
  {
    id: 'managed-services',
    title: 'Managed Services',
    shortDesc: 'AI-driven delivery teams that ship on time. Intelligent automation built in.',
    Icon: Server,
    href: '/services/managed-services',
  },
  {
    id: 'capital-projects',
    title: 'Capital Projects',
    shortDesc: 'Large-scale infrastructure and program execution from build to operate.',
    Icon: Building2,
    href: '/capital-projects',
  },
  {
    id: 'staff-augmentation',
    title: 'Staff Augmentation',
    shortDesc: 'Senior engineers including AI/ML specialists. Deploy in weeks, not months.',
    Icon: Users,
    href: '/services/staff-augmentation',
  },
  {
    id: 'business-consulting',
    title: 'Business Consulting',
    shortDesc: 'AI strategy and transformation leadership. Execution, not PowerPoint decks.',
    Icon: Briefcase,
    href: '/services/business-consulting',
  },
];

// ─── Technology Ecosystem — Portkey-style premium showcase ────────────────
// Real brand logos stored locally in /public/tech-logos/ for reliability
// (Simple Icons CDN drops some major brands due to trademark policy).
const TECH_TOP_ROW = [
  { name: 'OpenAI',          file: 'openai',     tag: 'AI Platform',  desc: 'Frontier LLM delivery'           },
  { name: 'Anthropic',       file: 'anthropic',  tag: 'AI Platform',  desc: 'Constitutional AI & Claude'      },
  { name: 'AWS',             file: 'aws',        tag: 'Cloud',        desc: 'Cloud-native delivery at scale'  },
  { name: 'Microsoft Azure', file: 'azure',      tag: 'Cloud',        desc: 'Enterprise cloud + AI services'  },
  { name: 'Snowflake',       file: 'snowflake',  tag: 'Data Cloud',   desc: 'AI-ready data warehouse'         },
  { name: 'Databricks',      file: 'databricks', tag: 'Lakehouse',    desc: 'Unified data + ML platform'      },
];

const TECH_BOTTOM_ROW = [
  { name: 'Docker',          file: 'docker',     tag: 'Containers',   desc: 'Container fundamentals'          },
  { name: 'Kubernetes',      file: 'kubernetes', tag: 'Orchestration',desc: 'Production-grade orchestration'  },
  { name: 'Salesforce',      file: 'salesforce', tag: 'CRM',          desc: 'Sales & service cloud delivery'  },
  { name: 'ServiceNow',      file: 'servicenow', tag: 'ITSM',         desc: 'Enterprise workflow automation'  },
  { name: 'SAP',             file: 'sap',        tag: 'ERP',          desc: 'Enterprise resource planning'    },
  { name: 'Oracle Cloud',    file: 'oracle',     tag: 'Cloud + DB',   desc: 'Enterprise database & cloud'     },
];

// Single logo chip — large square glass tile, logo only (no text).
const TechLogoChip = ({ name, file, isActive }) => (
  <div
    className={`tech-eco-chip shrink-0${isActive ? ' is-active' : ''}`}
    data-testid={`tech-eco-chip-${file}`}
    title={name}
    aria-label={name}
  >
    <img
      src={`/tech-logos/${file}.svg`}
      alt={`${name} logo`}
      draggable="false"
      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
    />
  </div>
);

const TechEcosystem = () => {
  // Indices of the logos currently nearest the horizontal center of the viewport
  // for each row — updated in sync with the marquee transform.
  const [activeTop, setActiveTop] = useState(0);
  const [activeBot, setActiveBot] = useState(0);
  const topTrackRef = useRef(null);
  const botTrackRef = useRef(null);

  useEffect(() => {
    let raf;
    let lastTick = 0;
    let lastTop = -1, lastBot = -1;

    const findCenteredIndex = (track, totalUnique) => {
      if (!track) return 0;
      const chips = track.children;
      const cx = window.innerWidth / 2;
      let bestIdx = 0;
      let bestD = Infinity;
      for (let i = 0; i < chips.length; i++) {
        const r = chips[i].getBoundingClientRect();
        // Skip chips clearly off-screen — saves measurement cost
        if (r.right < -50 || r.left > window.innerWidth + 50) continue;
        const m = r.left + r.width / 2;
        const d = Math.abs(m - cx);
        if (d < bestD) { bestD = d; bestIdx = i % totalUnique; }
      }
      return bestIdx;
    };

    const tick = (now) => {
      // Throttle to ~12 FPS — plenty for a name swap and saves the main thread
      if (now - lastTick > 80) {
        const t = findCenteredIndex(topTrackRef.current, TECH_TOP_ROW.length);
        const b = findCenteredIndex(botTrackRef.current, TECH_BOTTOM_ROW.length);
        if (t !== lastTop) { setActiveTop(t); lastTop = t; }
        if (b !== lastBot) { setActiveBot(b); lastBot = b; }
        lastTick = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const topActive = TECH_TOP_ROW[activeTop];
  const botActive = TECH_BOTTOM_ROW[activeBot];

  return (
    <div className="tech-ecosystem mt-16 lg:mt-20" data-testid="tech-ecosystem">
      <div className="tech-eco-bg-glow" aria-hidden="true" />

      {/* ── Top marquee (logos only) ────────────────────────────────── */}
      <div
        className="tech-eco-row tech-eco-row--top"
        style={{
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
          maskImage:       'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
        }}
      >
        <div ref={topTrackRef} className="tech-eco-track tech-eco-track--left-36">
          {[...TECH_TOP_ROW, ...TECH_TOP_ROW, ...TECH_TOP_ROW].map((t, i) => (
            <TechLogoChip
              key={`top-${i}`}
              {...t}
              isActive={(i % TECH_TOP_ROW.length) === activeTop}
            />
          ))}
        </div>
      </div>

      {/* ── Center stage: connector SVG + glass panel ──────────────── */}
      <div className="tech-eco-stage">
        <svg
          className="tech-eco-connectors hidden lg:block"
          viewBox="0 0 1200 360"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="tech-eco-line-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#67E8F9" stopOpacity="0" />
              <stop offset="40%"  stopColor="#67E8F9" stopOpacity="1" />
              <stop offset="60%"  stopColor="#C4B5FD" stopOpacity="1" />
              <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0" />
            </linearGradient>
            <filter id="tech-eco-comet-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Static faint base lines */}
          <path d="M 250  0  C 320 120, 400 140, 500 180"   className="tech-eco-line-base" pathLength="100" />
          <path d="M 600  0  C 600  90, 600 130, 600 180"   className="tech-eco-line-base" pathLength="100" />
          <path d="M 950  0  C 880 120, 800 140, 700 180"   className="tech-eco-line-base" pathLength="100" />
          <path d="M 500 180 C 400 220, 320 240, 250 360"   className="tech-eco-line-base" pathLength="100" />
          <path d="M 600 180 C 600 230, 600 270, 600 360"   className="tech-eco-line-base" pathLength="100" />
          <path d="M 700 180 C 800 220, 880 240, 950 360"   className="tech-eco-line-base" pathLength="100" />

          {/* Traveling comet pulses — same paths, animated */}
          <path d="M 250  0  C 320 120, 400 140, 500 180"   className="tech-eco-comet"                pathLength="100" />
          <path d="M 600  0  C 600  90, 600 130, 600 180"   className="tech-eco-comet tec-d1"         pathLength="100" />
          <path d="M 950  0  C 880 120, 800 140, 700 180"   className="tech-eco-comet tec-d2"         pathLength="100" />
          <path d="M 500 180 C 400 220, 320 240, 250 360"   className="tech-eco-comet tec-d3"         pathLength="100" />
          <path d="M 600 180 C 600 230, 600 270, 600 360"   className="tech-eco-comet tec-d4"         pathLength="100" />
          <path d="M 700 180 C 800 220, 880 240, 950 360"   className="tech-eco-comet tec-d5"         pathLength="100" />
        </svg>

        <div className="tech-eco-panel" data-testid="tech-eco-panel">
          <div className="tech-eco-panel-shine" aria-hidden="true" />

          <div className="tech-eco-panel-header">
            <h3 className="tech-eco-panel-title">
              The stack we <span className="tech-eco-italic">staff &amp; deliver</span> on.
            </h3>
            <p className="tech-eco-panel-sub">
              Delivering talent solutions across modern cloud, AI, enterprise, and digital platforms.
            </p>
          </div>

          {/* Live readouts — bound to the centered logo in each marquee */}
          <div className="tech-eco-readouts">
            <div className="tech-eco-readout">
              <div className="tech-eco-readout-label">
                <span className="tech-eco-readout-dot tech-eco-readout-dot--cyan" />
                Currently staffing
              </div>
              <div className="tech-eco-readout-name" key={`top-${activeTop}`} data-testid="tech-eco-active-top">
                {topActive.name}
                <span className="tech-eco-readout-tag">{topActive.tag}</span>
              </div>
              <div className="tech-eco-readout-desc" key={`top-d-${activeTop}`}>
                {topActive.desc}
              </div>
            </div>

            <div className="tech-eco-readout-divider" aria-hidden="true" />

            <div className="tech-eco-readout">
              <div className="tech-eco-readout-label">
                <span className="tech-eco-readout-dot tech-eco-readout-dot--violet" />
                Currently operating
              </div>
              <div className="tech-eco-readout-name" key={`bot-${activeBot}`} data-testid="tech-eco-active-bot">
                {botActive.name}
                <span className="tech-eco-readout-tag">{botActive.tag}</span>
              </div>
              <div className="tech-eco-readout-desc" key={`bot-d-${activeBot}`}>
                {botActive.desc}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom marquee (logos only) ─────────────────────────────── */}
      <div
        className="tech-eco-row tech-eco-row--bottom"
        style={{
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
          maskImage:       'linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)',
        }}
      >
        <div ref={botTrackRef} className="tech-eco-track tech-eco-track--right-44">
          {[...TECH_BOTTOM_ROW, ...TECH_BOTTOM_ROW, ...TECH_BOTTOM_ROW].map((t, i) => (
            <TechLogoChip
              key={`bot-${i}`}
              {...t}
              isActive={(i % TECH_BOTTOM_ROW.length) === activeBot}
            />
          ))}
        </div>
      </div>
    </div>
  );
};


const IndustryCard = ({ item, pulseDelay = 0 }) => {
  const { name, outcome, Icon } = item;
  return (
    <div
      className="industry-card shrink-0 w-[180px] sm:w-[200px] lg:w-[220px]"
      data-testid={`industry-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="industry-card-inner">
        {/* Icon with pulse halo */}
        <div className="industry-icon-wrap">
          <span
            className="industry-icon-pulse"
            style={{ animationDelay: `${pulseDelay}s` }}
            aria-hidden="true"
          />
          <Icon className="industry-icon" strokeWidth={1.4} />
        </div>
        {/* Name */}
        <div className="industry-name">{name}</div>
        {/* Outcome */}
        <div className="industry-outcome">{outcome}</div>
      </div>
    </div>
  );
};

// ─── Tech card — smaller pill/card hybrid, cooler glass tint ──────────────
// (Legacy component — superseded by TechEcosystem. Kept removed to keep file lean.)

const Home = () => {
  const observerRef = useRef(null);
  const heroRef = useRef(null);
  const oRef = useRef(null);
  const videoCircleRef = useRef(null);
  const rightColRef = useRef(null);
  const [heroProgress, setHeroProgress] = useState(0);
  const [oAnchor, setOAnchor] = useState({ x: 0, y: 0, r: 60 });
  const [viewport, setViewport] = useState({ w: 1, h: 1 });
  // Right column bbox in viewport coords — drives the clip-path that initially
  // contains the video/mask within the right column, then expands to full viewport.
  const [rightColBbox, setRightColBbox] = useState({
    top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0
  });

  // Measure the *visible* video circle (not the outer O span) so the scroll-mask
  // peephole starts at the exact same position and radius as the inline portal —
  // resulting in a perfectly seamless takeover with zero snap.
  // We use ResizeObserver + IntersectionObserver-friendly remeasure to stay in sync
  // with font loading, viewport resize, and any layout reflow.
  useLayoutEffect(() => {
    const measure = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      const target = videoCircleRef.current || oRef.current;
      if (target) {
        const r = target.getBoundingClientRect();
        setOAnchor({
          x: r.left + r.width / 2,
          y: r.top + r.height / 2,
          r: Math.min(r.width, r.height) / 2,
        });
      }
      // Right-column bbox — used by the scroll-driven mask stage to size its
      // clip-path to the right column initially, then expand to full viewport.
      if (rightColRef.current) {
        const rc = rightColRef.current.getBoundingClientRect();
        setRightColBbox({
          top: rc.top,
          left: rc.left,
          right: rc.right,
          bottom: rc.bottom,
          width: rc.width,
          height: rc.height,
        });
      }
    };

    measure();
    window.addEventListener('resize', measure);

    // Re-measure when fonts finish loading (Anton arrives async and shifts layout).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => requestAnimationFrame(() => requestAnimationFrame(measure)))
        .catch(() => {});
    }

    // Track the actual circle DOM with ResizeObserver — captures any reflow.
    let ro;
    let roRC;
    if (videoCircleRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(videoCircleRef.current);
    }
    if (rightColRef.current && typeof ResizeObserver !== 'undefined') {
      roRC = new ResizeObserver(() => measure());
      roRC.observe(rightColRef.current);
    }
    // Continuous measurement during scroll — right-col top/bottom move with scroll
    const onScrollMeasure = () => measure();
    window.addEventListener('scroll', onScrollMeasure, { passive: true });

    // Late safety remeasures to catch async layout settle on slow networks.
    const t1 = setTimeout(measure, 200);
    const t2 = setTimeout(measure, 800);
    const t3 = setTimeout(measure, 2000);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', onScrollMeasure);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (ro) ro.disconnect();
      if (roRC) roRC.disconnect();
    };
  }, []);

  // Pinned scroll progress 0 → 1 (over heroSection.height − 100vh)
  // We separate the *raw* scroll position from a smoothed value that lerps toward it
  // every frame. This produces a buttery, cinematic transition independent of scroll
  // input cadence (mouse wheel vs trackpad vs keyboard).
  useEffect(() => {
    let raf = 0;
    let target = 0;
    let current = 0;
    const SMOOTH = 0.20; // ~80ms critically-damped feel — snappier convergence

    const computeTarget = () => {
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = Math.max(1, rect.height - window.innerHeight);
      target = Math.max(0, Math.min(1, -rect.top / total));
    };

    const tick = () => {
      // Critically-damped lerp toward target
      const delta = target - current;
      if (Math.abs(delta) > 0.00025) {
        current += delta * SMOOTH;
        setHeroProgress(current);
      } else if (current !== target) {
        current = target;
        setHeroProgress(current);
      }
      raf = requestAnimationFrame(tick);
    };

    const onScroll = () => computeTarget();

    computeTarget();
    current = target;
    setHeroProgress(target);
    raf = requestAnimationFrame(tick);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // ─── Hero scroll narrative — strict choreography (350vh hero) ──────────
  // 0–10%   ENTRY     full dark hero, INFOTRON+underline visible
  // 10–20%  WIPE      white overlay ramps 0→1 across viewport
  // 20–35%  BUILD-UP  underline lifts upward + fades; text stays fixed
  // 40–55%  SCALE     INFOTRON scales smoothly, drifts to viewport centre
  // 55–68%  TAKEOVER  scale → 9, mask dissolves, full-bleed video reveals
  // 68–100% HOLD      fullscreen video held (user scrolls 1–2x before release)
  const p = heroProgress;

  // White wipe — stays at 1 from p≥0.20 until takeover, then fades for video
  const wipeT = smoothstep(0.10, 0.20, p);
  const whiteWipeOpacity = wipeT * (1 - smoothstep(0.55, 0.68, p));

  // Left content fades during the wipe on desktop only — on mobile the
  // canvas is hidden so the copy must remain fully visible at all times.
  const isMobile = viewport.w > 0 && viewport.w < 1024;
  const heroContentOpacity = isMobile ? 1 : (1 - smoothstep(0.05, 0.18, p));

  // Underline animation — single segment lifts upward + fades during build-up
  const underlineT  = smoothstep(0.20, 0.34, p);
  const underlineY  = -260 * underlineT;
  const underlineOp = 1 - underlineT;

  // INFOTRON scale — capped at 2.3× during scale phase so letters never crop.
  // Past that, the full-bleed video crossfades in to handle the takeover.
  let infotronScale = 1.0;
  if (p < 0.40) {
    infotronScale = 1.0;
  } else if (p < 0.62) {
    infotronScale = lerp(1.0, 2.3, smoothstep(0.40, 0.62, p));
  } else {
    infotronScale = 2.3;                                // HOLD — fullscreen video takes over
  }

  // Final un-masked full-bleed video — fades in across takeover, holds at 1
  const fullscreenVideoOpacity = smoothstep(0.55, 0.68, p);

  useEffect(() => {
    // Trigger animations slightly BEFORE the element enters the viewport
    // (rootMargin bottom: +120px) so the user sees content already animating
    // by the time it scrolls into view — eliminates the "waiting for content
    // to appear" feel. threshold:0 = fire as soon as any pixel intersects.
    const options = {
      threshold: 0,
      rootMargin: '0px 0px 120px 0px'
    };

    observerRef.current = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          entry.target.classList.remove('scroll-reveal');
          // Unobserve immediately so the browser frees the layer + observer slot
          // (one-shot reveal — no retrigger). Critical for mobile GPU memory.
          obs.unobserve(entry.target);
        }
      });
    }, options);

    document.querySelectorAll('.scroll-reveal').forEach(el => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Dedicated observer for engagement cards — re-triggers the fold-open
  // animation every time a card enters the viewport. To avoid the card
  // visibly resetting while still partly on screen, we only strip the
  // `.is-revealed` class when the card has fully left the viewport
  // (intersectionRatio === 0).
  useEffect(() => {
    const cards = document.querySelectorAll('.engagement-card-reveal');
    if (!cards.length) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          entry.target.classList.add('is-revealed');
        } else if (!entry.isIntersecting) {
          // Fully out of viewport — safe to reset for next reveal
          entry.target.classList.remove('is-revealed');
        }
      });
    }, { threshold: [0, 0.25] });

    cards.forEach(card => obs.observe(card));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Outcomes. Not Headcount."
        description="Infotron Solutions delivers Managed Services, Staff Augmentation, Business Consulting, and Capital Projects execution for enterprise clients. Delivery-first. Outcome-owned."
        path="/"
        keywords="Infotron Solutions, managed services, staff augmentation, business consulting, capital projects, enterprise IT, technology delivery, outcomes"
        schema={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Infotron Solutions",
          "url": "https://infotronsolutions.com",
          "image": "https://customer-assets.emergentagent.com/job_ba897003-eeca-4b0e-8e12-dd77cec76f35/artifacts/mcntdahb_INFOTRON%20Gradient%20Logo%20cropped.png",
          "description": "Enterprise IT services and talent solutions firm delivering Managed Services, Staff Augmentation, Business Consulting, and Capital Projects execution.",
          "telephone": "+1-775-305-9399",
          "email": "contact@infotronsolutions.com",
          "areaServed": ["US", "GB", "CA", "IN"],
          "serviceType": [
            "Managed Services",
            "Staff Augmentation",
            "Business Consulting",
            "Capital Projects"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Service offerings",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Managed Services", "url": "https://infotronsolutions.com/services/managed-services" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Staff Augmentation", "url": "https://infotronsolutions.com/services/staff-augmentation" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Business Consulting", "url": "https://infotronsolutions.com/services/business-consulting" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Capital Projects", "url": "https://infotronsolutions.com/capital-projects" } }
            ]
          }
        }}
      />
      {/* HERO SECTION — Pinned scroll on lg+ ONLY. Mobile shows simple hero
          (no canvas, no video, no scroll choreography) and flows directly
          into the next section.                                            */}
      <section
        ref={heroRef}
        className="relative lg:h-[350vh]"
      >
        <div
          className="relative lg:sticky lg:top-0 lg:h-screen w-full flex items-center overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #050B1A 0%, #0A192F 35%, #1E3A8A 70%, #4C1D95 100%)' }}
        >

        {/* ─── HERO CINEMATIC LAYER (DESKTOP ONLY) ──────────────────────── */}
        <div
          className="hidden lg:block absolute inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 25 }}
          aria-hidden="true"
          data-testid="hero-canvas"
        >
          {/* Layer A — Global white wipe (covers entire viewport, including left
              copy, exactly as spec requires). Premium white with subtle depth. */}
          <div
            className="absolute inset-0"
            style={{
              opacity: whiteWipeOpacity,
              background:
                'radial-gradient(ellipse 60% 55% at 70% 50%, rgba(255,255,255,0.85) 0%, rgba(250,251,253,0.0) 75%), linear-gradient(180deg, #FAFBFD 0%, #F4F6FB 50%, #EDF1F8 100%)',
              willChange: 'opacity',
            }}
          />

          {/* Layers B + C + D — INFOTRON mask + asymmetric brand frame.
              ViewBox locked to the live viewport size for 1:1 pixel mapping
              regardless of screen aspect. All positions are viewport-relative. */}
          {(() => {
            const VW = Math.max(1, viewport.w);
            const VH = Math.max(1, viewport.h);
            // Wordmark — initially right-weighted; drifts to viewport centre
            // BEFORE scale begins so letters scale from centre and never crop.
            const driftT = smoothstep(0.20, 0.40, p);
            const tx = lerp(VW * 0.70, VW * 0.50, driftT);
            const ty = VH * 0.50;
            const fs = VH * 0.18;
            // Underline — CENTERED below text, ~65% of text width.
            const textWidthApprox = fs * 4.0 + 42; // Anton + letter-spacing 6
            const ulWidth  = textWidthApprox * 0.65;
            const ulLeft   = tx - ulWidth / 2;     // centred under text
            const ulRight  = tx + ulWidth / 2;
            const ulY      = ty + fs * 0.62;       // small gap below baseline
            return (
              <svg
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="none"
                viewBox={`0 0 ${VW} ${VH}`}
              >
                <defs>
                  <linearGradient
                    id="brand-grad"
                    x1="0" y1="0" x2={VW} y2={VH}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor="#EC4899" />
                    <stop offset="50%" stopColor="#A855F7" />
                    <stop offset="100%" stopColor="#3B82F6" />
                  </linearGradient>
                  <mask id="hero-infotron-mask" maskUnits="userSpaceOnUse">
                    <rect x="0" y="0" width={VW} height={VH} fill="black" />
                    <text
                      x={tx} y={ty}
                      fontFamily="'Anton', 'Bebas Neue', 'Inter', system-ui, sans-serif"
                      fontWeight="900"
                      fontSize={fs}
                      letterSpacing="6"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="white"
                    >
                      INFOTRON
                    </text>
                  </mask>
                </defs>

                {/* Single clean underline — centered below text. Draws on once
                    on page load via stroke-dasharray (CSS animation). */}
                <line
                  className="hero-underline"
                  x1={ulLeft}
                  y1={ulY + underlineY}
                  x2={ulRight}
                  y2={ulY + underlineY}
                  stroke="url(#brand-grad)"
                  strokeWidth="3"
                  strokeLinecap="square"
                  opacity={underlineOp}
                  style={{ '--ul-length': `${ulWidth}px` }}
                />

                {/* Video masked by INFOTRON, scaled around text centre */}
                <g
                  transform={`translate(${tx} ${ty}) scale(${infotronScale.toFixed(4)}) translate(${-tx} ${-ty})`}
                  style={{ willChange: 'transform' }}
                >
                  <foreignObject
                    x="0" y="0" width={VW} height={VH}
                    mask="url(#hero-infotron-mask)"
                  >
                    <video
                      xmlns="http://www.w3.org/1999/xhtml"
                      autoPlay muted loop playsInline preload="auto"
                      data-testid="hero-portal-video"
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center',
                        display: 'block',
                      }}
                    >
                      <source src="/videos/hero.mp4" type="video/mp4" />
                    </video>
                  </foreignObject>
                </g>
              </svg>
            );
          })()}

          {/* Final stage — un-masked full-bleed video for the clean reveal */}
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="hero-fullscreen-video"
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
              opacity: fullscreenVideoOpacity,
              willChange: 'opacity',
            }}
          >
            <source src="/videos/hero.mp4" type="video/mp4" />
          </video>
        </div>
        {/* Animated mesh glow layer */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
            style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)', animation: 'float-slow 14s ease-in-out infinite' }}
          />
          <div
            className="absolute top-1/4 right-0 w-[640px] h-[640px] rounded-full opacity-30 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)', animation: 'float-slow 18s ease-in-out infinite reverse' }}
          />
          <div
            className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl"
            style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)', animation: 'float-slow 22s ease-in-out infinite' }}
          />
        </div>

        {/* Bottom vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050B1A]/60 pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 lg:pt-32 pb-8 lg:pb-16 relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-16 items-center">
            {/* Left — Copy */}
            <div className="lg:col-span-7 animate-fade-in-up will-change-[opacity] text-center lg:text-left" style={{ opacity: heroContentOpacity }}>
              <h1 className="text-[clamp(2.5rem,5.2vw,4.75rem)] font-black text-white leading-[1.05] mb-6 tracking-[-0.02em]">
                <span className="bg-gradient-to-r from-blue-400 via-violet-300 to-blue-400 bg-clip-text text-transparent bg-[length:200%_auto]" style={{ animation: 'shimmer 6s linear infinite' }}>
                  Outcomes.
                </span>
                <br />
                <span className="text-white">Not Headcount.</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-200 mb-5 leading-relaxed max-w-2xl mx-auto lg:mx-0 font-medium">
                Full-stack engineering teams that own delivery.
              </p>
              <p className="text-base lg:text-lg text-gray-400 mb-10 max-w-2xl mx-auto lg:mx-0">
                Built for technology leaders who measure results, not hours.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center sm:items-stretch justify-center lg:justify-start">
                <Link to="/contact?type=client" data-testid="hero-primary-cta">
                  <Button
                    size="lg"
                    className="relative bg-gradient-to-r from-blue-600 to-violet-500 text-white hover:shadow-[0_10px_40px_-10px_rgba(59,130,246,0.8)] text-base font-semibold px-10 py-7 rounded-lg transition-all duration-300 hover:-translate-y-0.5 group overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Talk to Our Team
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-violet-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Button>
                </Link>
                <Link to="/careers" data-testid="hero-secondary-cta">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border border-white/20 bg-white/[0.03] text-white hover:bg-white/[0.08] hover:border-white/40 text-base font-semibold px-10 py-7 rounded-lg transition-all duration-300 backdrop-blur"
                  >
                    View Open Positions
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right — Layout-only spacer for the desktop cinematic canvas
                (which is rendered as an absolutely-positioned layer above
                this section). Hidden on mobile so the hero is compact.   */}
            <div
              ref={rightColRef}
              className="hidden lg:block lg:col-span-5 relative my-8 min-h-[460px]"
              data-testid="hero-right-column"
            />
          </div>
        </div>
        </div>
      </section>

      {/* ─── TECHNOLOGY ECOSYSTEM ─── Standalone premium section ───────────────
         Houses the AI Engineers On Demand highlight + the separator heading +
         the existing TechEcosystem showcase. */}
      <section
        className="relative py-24 lg:py-28 overflow-hidden"
        data-testid="tech-ecosystem-section"
        style={{
          background: `
            radial-gradient(ellipse 80% 55% at 50% 30%, #0F1B3D 0%, #08122A 60%, #050917 100%),
            #050917
          `,
        }}
      >
        {/* Faint dot grid for premium texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #67E8F9 1px, transparent 0)',
            backgroundSize: '44px 44px',
          }}
        />
        {/* Aurora corner halos */}
        <div className="absolute -top-32 -left-32 w-[620px] h-[620px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.45) 0%, transparent 70%)' }} />
        <div className="absolute -bottom-40 -right-32 w-[680px] h-[680px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.45) 0%, transparent 70%)' }} />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12">

          {/* Premium separator heading */}
          <div className="tech-eco-separator" data-testid="tech-eco-separator">
            <div className="tech-eco-separator-divider" aria-hidden="true">
              <span className="tech-eco-separator-dot" />
            </div>
            <h3 className="tech-eco-separator-title">
              Technology{' '}
              <span className="tech-eco-separator-grad">Ecosystem</span>
            </h3>
            <p className="tech-eco-separator-sub">
              Platforms powering modern digital transformation.
            </p>
          </div>

          <TechEcosystem />

          {/* ── AI Engineers On Demand — compact popup-style card BELOW the ecosystem ── */}
          <div className="ai-engineers-highlight ai-engineers-highlight--compact scroll-reveal" data-testid="ai-engineers-highlight">
            <div className="ai-eng-glow" aria-hidden="true" />
            <div className="ai-eng-inner">
              <div className="ai-eng-microlabel">
                <span className="ai-eng-microlabel-dot" />
                Recommended by Industry Leaders
              </div>
              <div className="ai-eng-headline-row">
                <div className="ai-eng-orbit" aria-hidden="true">
                  <span className="ai-eng-orbit-core" />
                  <span className="ai-eng-orbit-ring ai-eng-orbit-ring-1" />
                  <span className="ai-eng-orbit-ring ai-eng-orbit-ring-2" />
                  <span className="ai-eng-orbit-spark" />
                </div>
                <div className="ai-eng-text">
                  <h3 className="ai-eng-title">
                    AI Engineers{' '}
                    <span className="ai-eng-title-grad">On Demand</span>
                  </h3>
                  <p className="ai-eng-sub">
                    Trusted by modern enterprises building next-generation AI teams.
                  </p>
                </div>
                <Link
                  to="/contact?type=client&service=ai-engineers"
                  className="ai-eng-cta"
                  data-testid="ai-engineers-cta"
                >
                  Talk to delivery
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build. Operate. Transfer. — Premium timeline visual */}
      <section className="relative py-24 bg-gradient-to-b from-[#F1F5FB] via-[#F8FAFC] to-[#F1F5FB] overflow-hidden">
        {/* Soft aurora highlights in negative space */}
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-50 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.20) 0%, transparent 70%)' }} />
        <div className="absolute -bottom-32 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-50 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.20) 0%, transparent 70%)' }} />

        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">

          {/* Header */}
          <div className="text-center mb-16 scroll-reveal">
            <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 bg-white/70 backdrop-blur mb-5">
              Delivery Model
            </span>
            <h2
              className="text-4xl lg:text-6xl font-semibold text-slate-900 mb-4"
              style={{
                fontFamily: "'Playfair Display', 'Libre Baskerville', Georgia, serif",
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              Build.{' '}
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent italic" style={{ fontWeight: 600 }}>
                Operate.
              </span>{' '}
              Transfer.
            </h2>
            <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Speed now. Ownership later. A delivery model that reduces execution risk and ensures continuity, without vendor lock-in.
            </p>
          </div>

          {/* Timeline visual — 3 phases with an animated connector */}
          <div className="relative">
            {/* Horizontal connector line (desktop) */}
            <div className="hidden lg:block absolute top-[68px] left-[16.66%] right-[16.66%] h-px pointer-events-none">
              <div className="bot-connector" />
            </div>

            <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 relative">
              {/* Phase 1 — Build */}
              <div className="bot-phase group scroll-reveal delay-100" data-testid="bot-build">
                <div className="bot-phase-disc">
                  <span className="bot-phase-letter" style={{ color: '#2563EB' }}>B</span>
                </div>
                <h3 className="bot-phase-title">Build</h3>
                <p className="bot-phase-desc">Assemble and onboard senior, outcome-driven teams</p>
                <div className="bot-phase-meta">
                  <span className="bot-phase-chip">Senior pods</span>
                  <span className="bot-phase-chip">2–4 weeks</span>
                </div>
              </div>

              {/* Phase 2 — Operate */}
              <div className="bot-phase group scroll-reveal delay-200" data-testid="bot-operate">
                <div className="bot-phase-disc bot-phase-disc--mid">
                  <span className="bot-phase-letter" style={{ color: '#7C3AED' }}>O</span>
                </div>
                <h3 className="bot-phase-title">Operate</h3>
                <p className="bot-phase-desc">Own delivery, quality and execution with full accountability</p>
                <div className="bot-phase-meta">
                  <span className="bot-phase-chip">SLA-backed</span>
                  <span className="bot-phase-chip">AI-augmented</span>
                </div>
              </div>

              {/* Phase 3 — Transfer */}
              <div className="bot-phase group scroll-reveal delay-300" data-testid="bot-transfer">
                <div className="bot-phase-disc bot-phase-disc--end">
                  <span className="bot-phase-letter" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>T</span>
                </div>
                <h3 className="bot-phase-title">Transfer</h3>
                <p className="bot-phase-desc">Seamlessly transition teams, systems and knowledge in-house</p>
                <div className="bot-phase-meta">
                  <span className="bot-phase-chip">Zero lock-in</span>
                  <span className="bot-phase-chip">Knowledge handoff</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ─── INDUSTRIES WE SERVE ─── Premium infinite-loop marquee (2 rows, opposite directions) */}
      <section
        className="relative py-28 lg:py-32 bg-[#050B1A] overflow-hidden"
        data-testid="industries-section"
      >
        {/* Soft ambient glow layers — never decorative noise, just depth */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              'radial-gradient(ellipse 70% 55% at 15% 30%, rgba(37,99,235,0.14) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 85% 70%, rgba(124,58,237,0.14) 0%, transparent 60%)',
          }}
        />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-16 lg:mb-20 scroll-reveal">
            <h2
              className="text-4xl lg:text-6xl font-semibold text-white mb-6"
              style={{
                fontFamily:
                  "'Playfair Display', 'Libre Baskerville', Georgia, serif",
                letterSpacing: '-0.018em',
                lineHeight: 1.08,
              }}
            >
              Industries We{' '}
              <span
                className="bg-gradient-to-r from-blue-400 via-indigo-400 to-violet-400 bg-clip-text text-transparent italic"
                style={{ fontWeight: 600 }}
              >
                Serve
              </span>
            </h2>
            <p
              className="text-base lg:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed"
              style={{
                fontFamily:
                  "'Inter', 'Neue Haas Grotesk', system-ui, -apple-system, sans-serif",
                letterSpacing: '-0.005em',
              }}
            >
              Built for outcomes.
              <br className="hidden sm:inline" />{' '}
              Delivered by experts.
              <br className="hidden sm:inline" />{' '}
              Accelerated by AI.
            </p>
          </div>

          {/* Marquee viewport — edge fade mask applied here */}
          <div
            className="relative"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)',
              maskImage:
                'linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)',
            }}
          >
            {/* INDUSTRIES — single continuous row moving LEFT, 55s */}
            <div className="industries-marquee-viewport">
              <div className="industries-marquee-track industries-marquee-track--left-55">
                {[...industries, ...industries].map((item, i) => (
                  <IndustryCard
                    key={`ind-${i}`}
                    item={item}
                    pulseDelay={(i % industries.length) * 0.8}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* BOLD SERVICES SECTION - Consulting Style */}
      <section className="relative py-32 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-emerald-900/30 to-cyan-900/30 rounded-full filter blur-3xl" />
        
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative z-10">
          {/* Section Header - BOLD */}
          <div className="text-center mb-20 scroll-reveal">
            <div className="inline-block mb-6">
              <span className="text-blue-500 font-black text-sm tracking-[0.2em] uppercase">How We Work</span>
            </div>
            <h2 className="text-5xl lg:text-7xl font-black text-white mb-8 leading-tight">
              Four Engagement<br/>Models. One Goal.
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Your results. Our accountability.
            </p>
            <div className="w-32 h-2 bg-gradient-to-r from-blue-600 to-violet-500 mx-auto rounded-full" />
          </div>

          {/* Engagement Models Grid — 4 cards in one row on desktop, mirrors Services dropdown order */}
          <div className="flex justify-center engagement-grid-perspective">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7 w-full max-w-[1500px]">
            {engagementModels.map((model, index) => {
              const IconComponent = model.Icon;
              // Subtle accent variation only on the icon glow — keeps card body identical
              const accentGlows = [
                'from-blue-400/30 to-blue-500/10',
                'from-cyan-400/30 to-blue-500/10',
                'from-violet-400/30 to-blue-500/10',
                'from-blue-400/30 to-violet-500/10',
              ];
              const delayMs = index * 140;

              return (
                <Link
                  key={model.id}
                  to={model.href}
                  data-testid={`engagement-card-${model.id}`}
                  style={{ transitionDelay: `${delayMs}ms` }}
                  className={`engagement-card feature-card group relative flex flex-col p-8 lg:p-9 rounded-2xl bg-gradient-to-br from-[#1E3A8A] via-[#2E4BA8] to-[#4C3CA8] border border-white/10 shadow-xl shadow-blue-900/20 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/30 hover:border-white/20 overflow-hidden engagement-card-reveal`}
                >
                  {/* Top thin accent line */}
                  <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

                  {/* Unified dot pattern */}
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none" aria-hidden="true">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                      backgroundSize: '40px 40px'
                    }} />
                  </div>

                  {/* Subtle hover glow (same for all cards) */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white/0 to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Icon — uniform sizing across all 4 cards */}
                    <div className="mb-7">
                      <div className="relative w-16 h-16 rounded-xl flex items-center justify-center bg-white/15 backdrop-blur-sm border border-white/15 group-hover:scale-105 transition-all duration-500">
                        <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${accentGlows[index]} opacity-60`} />
                        <IconComponent className="w-8 h-8 text-white relative z-10" strokeWidth={2} />
                      </div>
                    </div>

                    {/* Title — consistent 2-line cap */}
                    <h3 className="text-2xl lg:text-[26px] font-black text-white mb-4 leading-[1.15] min-h-[64px]">
                      {model.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[15px] lg:text-base text-white/85 leading-relaxed mb-8 font-medium flex-1">
                      {model.shortDesc}
                    </p>

                    {/* CTA — pinned to bottom of card */}
                    <div className="flex items-center gap-2.5 text-white font-bold group-hover:gap-4 transition-all duration-300 mt-auto">
                      <span className="text-[15px]">Explore</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" strokeWidth={2.5} />
                    </div>
                  </div>
                </Link>
              );
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Infotron */}
      <section className="py-32 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">
        <div className="absolute inset-0 geometric-pattern opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-20 scroll-reveal">
            <h2
              className="text-4xl lg:text-6xl font-semibold mb-7 text-slate-900"
              style={{
                fontFamily: "'Playfair Display', 'Libre Baskerville', Georgia, serif",
                letterSpacing: '-0.018em',
                lineHeight: 1.08,
              }}
            >
              Why{' '}
              <span
                className="bg-gradient-to-r from-blue-700 via-indigo-600 to-purple-700 bg-clip-text text-transparent italic"
                style={{ fontWeight: 600 }}
              >
                Infotron
              </span>
              ?
            </h2>
            <p
              className="text-lg lg:text-[19px] text-slate-500 max-w-2xl mx-auto"
              style={{
                fontFamily: "'Inter', 'Neue Haas Grotesk', system-ui, -apple-system, sans-serif",
                lineHeight: 1.65,
                letterSpacing: '-0.005em',
                fontWeight: 400,
              }}
            >
              Not another staffing firm. A delivery partner with skin in the game.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {whyInfotron.map((item, index) => {
              const IconMap = { Zap, Target, Award, TrendingUp, Shield, Globe };
              const IconComponent = IconMap[item.icon];
              const num = String(index + 1).padStart(2, '0');
              return (
                <div
                  key={index}
                  className={`why-card group scroll-reveal delay-${index * 100 + 200}`}
                >
                  <span className="why-card-border" aria-hidden="true" />
                  <div className="why-card-inner">
                    {/* Watermark number — large, faint, slides on hover */}
                    <div className="why-card-num" aria-hidden="true">{num}</div>

                    {/* Icon + small dot row */}
                    <div className="why-card-icon-wrap">
                      <IconComponent className="why-card-icon" strokeWidth={1.8} />
                      <span className="why-card-icon-glow" aria-hidden="true" />
                    </div>

                    <h3 className="why-card-title">{item.title}</h3>
                    <p className="why-card-desc">{item.description}</p>

                    {/* Gradient underline that grows on hover */}
                    <div className="why-card-underline" aria-hidden="true" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise Outcomes Strip — Premium Editorial Panels */}
      <section className="py-20 bg-[#0A192F] relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="scroll-reveal">
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-blue-300/80 mb-3 block">
                How Enterprise Teams Win
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight max-w-2xl">
                Built for the pace and scale of real delivery.
              </h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Global Delivery */}
            <div
              className="group relative rounded-2xl overflow-hidden h-80 border border-white/10 hover:border-white/25 transition-colors duration-500 scroll-reveal scroll-reveal-card delay-100"
              data-testid="banner-global-delivery"
              style={{ background: 'linear-gradient(140deg, #0A192F 0%, #1E3A8A 55%, #2563EB 100%)' }}
            >
              {/* Abstract mesh glow */}
              <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full blur-3xl opacity-40 transition-opacity duration-700 group-hover:opacity-60"
                style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 65%)' }} />
              {/* Grid */}
              <div
                className="absolute inset-0 opacity-0"
                style={{ backgroundSize: '40px 40px' }}
              />
              {/* Orbit rings */}
              <svg className="absolute right-6 top-6 opacity-30" width="180" height="180" viewBox="0 0 180 180" fill="none">
                <circle cx="90" cy="90" r="70" stroke="#ffffff" strokeWidth="0.7" strokeDasharray="2 4" />
                <circle cx="90" cy="90" r="50" stroke="#ffffff" strokeWidth="0.7" />
                <circle cx="90" cy="90" r="30" stroke="#ffffff" strokeWidth="0.7" strokeDasharray="2 4" />
                <circle cx="90" cy="20" r="3" fill="#93C5FD" />
                <circle cx="160" cy="90" r="3" fill="#A78BFA" />
                <circle cx="90" cy="140" r="2.5" fill="#60A5FA" />
              </svg>

              <div className="relative h-full flex flex-col justify-between p-7">
                <span className="text-[11px] font-mono tracking-[0.22em] uppercase text-white/60">01 / Delivery</span>
                <div>
                  <div className="text-2xl lg:text-3xl font-bold text-white leading-snug mb-2">Global Delivery, Locally Aligned</div>
                  <div className="text-sm text-blue-100/80 leading-relaxed">
                    Four hubs across the US, Canada, UK, and India moving in your time zone.
                  </div>
                </div>
              </div>
            </div>

            {/* Ownership / Collaboration */}
            <div
              className="group relative rounded-2xl overflow-hidden h-80 border border-white/10 hover:border-white/25 transition-colors duration-500 scroll-reveal scroll-reveal-card delay-200"
              data-testid="banner-ownership"
              style={{ background: 'linear-gradient(140deg, #0A192F 0%, #3730A3 55%, #6D28D9 100%)' }}
            >
              <div className="absolute -bottom-24 -left-20 w-96 h-96 rounded-full blur-3xl opacity-50 transition-opacity duration-700 group-hover:opacity-70"
                style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 65%)' }} />
              {/* Network lines */}
              <svg className="absolute top-0 right-0 w-full h-full opacity-[0.22]" viewBox="0 0 400 400" preserveAspectRatio="none">
                <g stroke="#ffffff" strokeWidth="0.5" fill="none">
                  <path d="M 40 60 L 200 120 L 340 60" />
                  <path d="M 40 60 L 160 220 L 340 60" />
                  <path d="M 200 120 L 160 220 L 280 280" />
                  <path d="M 280 280 L 340 60" />
                </g>
                <g fill="#A78BFA">
                  <circle cx="40" cy="60" r="3.5" />
                  <circle cx="200" cy="120" r="3.5" />
                  <circle cx="340" cy="60" r="3.5" />
                  <circle cx="160" cy="220" r="3.5" />
                  <circle cx="280" cy="280" r="3.5" />
                </g>
              </svg>

              <div className="relative h-full flex flex-col justify-between p-7">
                <span className="text-[11px] font-mono tracking-[0.22em] uppercase text-white/60">02 / Ownership</span>
                <div>
                  <div className="text-2xl lg:text-3xl font-bold text-white leading-snug mb-2">Teams That Own Outcomes</div>
                  <div className="text-sm text-violet-100/80 leading-relaxed">
                    Senior engineers accountable for delivery, not just hours.
                  </div>
                </div>
              </div>
            </div>

            {/* Executive Delivery */}
            <div
              className="group relative rounded-2xl overflow-hidden h-80 border border-white/10 hover:border-white/25 transition-colors duration-500 scroll-reveal scroll-reveal-card delay-300"
              data-testid="banner-exec-delivery"
              style={{ background: 'linear-gradient(140deg, #050B1A 0%, #1E3A8A 60%, #3B82F6 100%)' }}
            >
              <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl opacity-35 transition-opacity duration-700 group-hover:opacity-55"
                style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 65%)' }} />
              {/* Bar chart metaphor */}
              <div className="absolute right-6 top-6 flex items-end gap-1.5 h-24">
                {[40, 62, 48, 78, 92, 70, 88].map((h, i) => (
                  <div
                    key={i}
                    className="w-2 rounded-sm bg-gradient-to-t from-blue-500/70 to-white/90"
                    style={{ height: `${h}%`, animation: `slide-in-left 1.4s ease-out ${0.08 * i}s both` }}
                  />
                ))}
              </div>

              <div className="relative h-full flex flex-col justify-between p-7">
                <span className="text-[11px] font-mono tracking-[0.22em] uppercase text-white/60">03 / Impact</span>
                <div>
                  <div className="text-2xl lg:text-3xl font-bold text-white leading-snug mb-2">Executive-Grade Delivery</div>
                  <div className="text-sm text-blue-100/80 leading-relaxed">
                    Senior leadership engaged from kickoff to transfer, measurable every step.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 scroll-reveal">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real engagements with Fortune 500 and high-growth companies
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.slice(0, 2).map((study, index) => (
              <Link
                key={study.id}
                to={`/case-studies/${study.slug}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 scroll-reveal scroll-reveal-card delay-${index * 100 + 200}`}
                data-testid={`home-case-study-${study.slug}`}
              >
                <div className="relative overflow-hidden h-64">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-[11px] font-semibold tracking-wider uppercase">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-6 text-white">
                    <div className="text-xs opacity-90">{study.client}</div>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-sm text-blue-600 font-semibold mb-3">{study.impact}</p>
                  <p className="text-gray-600 mb-6 line-clamp-2">{study.challenge}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-3 text-xs text-gray-500">
                      <span>{study.duration}</span>
                      <span>•</span>
                      <span>{study.teamSize}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 group-hover:gap-3 transition-all">
                      View Case Study
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12 scroll-reveal delay-500">
            <Link to="/resources">
              <Button size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
                View All Case Studies
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 bg-gray-900 text-white relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl" />
        </div>
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-20 scroll-reveal">
            <div className="inline-block mb-4">
              <span className="text-blue-400 font-semibold text-sm tracking-wider uppercase">Testimonials</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Executive perspectives from CTOs, CPOs, and technology leaders
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id} 
                className={`testimonial-card bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 scroll-reveal delay-${index * 100 + 200}`}
              >
                {/* Quote Icon */}
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                
                <p className="text-gray-300 leading-relaxed mb-8 italic text-lg">
                  "{testimonial.quote}"
                </p>
                
                <div className="pt-6 border-t border-gray-700">
                  <div className="font-semibold text-white text-lg">{testimonial.author}</div>
                  <div className="text-sm text-gray-400">{testimonial.title}, {testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-reveal">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Built for Enterprise Delivery
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Infotron Solutions is a US-headquartered IT services and talent solutions firm 
                serving Fortune 500 companies, high-growth SaaS platforms, FinTech innovators, 
                and institutional investors.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We've spent 15+ years building a reputation for execution excellence, 
                delivering mission-critical technology programs that scale businesses and 
                drive measurable ROI.
              </p>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
                  About Infotron
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            {/* Professional Team Imagery Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-xl overflow-hidden h-48 shadow-lg scroll-reveal scroll-reveal-image delay-100">
                <img 
                  src="https://images.unsplash.com/photo-1573497019418-b400bb3ab074?w=600&h=400&fit=crop&auto=format" 
                  alt="Professional business consultant" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden h-48 shadow-lg scroll-reveal scroll-reveal-image delay-200">
                <img 
                  src="https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?w=600&h=400&fit=crop&auto=format" 
                  alt="Team meeting in boardroom" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden h-48 shadow-lg scroll-reveal scroll-reveal-image delay-300">
                <img 
                  src="https://images.unsplash.com/photo-1580894732930-0babd100d356?w=600&h=400&fit=crop&auto=format" 
                  alt="Software engineer at work" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden h-48 shadow-lg scroll-reveal scroll-reveal-image delay-400">
                <img 
                  src="https://images.unsplash.com/photo-1573497491207-618cc224f243?w=600&h=400&fit=crop&auto=format" 
                  alt="Business professional leader" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0A192F 0%, #1E3A8A 40%, #3B82F6 75%, #7C3AED 100%)'}}>
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-white/10 rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full filter blur-3xl" style={{animation: 'float-slow 15s ease-in-out infinite'}} />
        </div>
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center relative z-10">
          <div className="scroll-reveal">
            <div className="inline-block mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold">
                Ready to Get Started?
              </span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Ready to Scale Your<br />Technology Capabilities?
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let's discuss how Infotron can accelerate your technology roadmap with 
              elite talent and execution-focused delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact?type=client">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-7 rounded-lg shadow-2xl hover:shadow-white/20 transition-all duration-300 hover:scale-105"
                >
                  <span className="flex items-center gap-3">
                    Schedule a Consultation
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Button>
              </Link>
              <Link to="/services/managed-services">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-10 py-7 rounded-lg transition-all duration-300"
                >
                  Explore Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
